"use client";

import { Check } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";

/* ── Demo data (this is a demo account — real seeded persona) ──────── */
const TASKS = [
  { t: "Review FDA Q-Sub response draft", cat: "Regulatory", p: "P1" },
  { t: "Send updated Series A data room to Greycroft", cat: "Investors", p: "P1" },
  { t: "Send VP Engineering offer letter to Maya", cat: "People", p: "P2" },
  { t: "CardioMonitor 2.1 beta sign-off with Mayo Clinic", cat: "Product", p: "P2" },
  { t: "Lead investor reference calls — finalise list", cat: "Investors", p: "P2" },
  { t: "Review architecture proposal — async device sync", cat: "Engineering", p: "P2" },
  { t: "Cleveland Clinic intro call", cat: "Sales & Pilots", p: "P2" },
];
const GOALS = [
  "Close Series A — $8M at $40M post",
  "Hire VP of Engineering & Head of Regulatory",
  "Ship CardioMonitor 2.1 to all customers",
];
const GOAL_DEADLINES = [
  "Hire VP of Engineering & Head of Regulatory · by July 1",
  "Ship CardioMonitor 2.1 · by June 30",
  "Onboard Mayo to production · by June 30",
];
const GOAL_SUGGESTIONS = [
  "Draft clinical evidence appendix for FDA Q-Sub (45 min)",
  "Block 60 min deep work — Series A narrative alignment",
];
const GOAL_CONN = [
  { g: "Close Series A — $8M at $40M post", d: "Data room update and reference calls support diligence." },
  { g: "Hire VP of Engineering", d: "The offer letter to Maya was a key milestone toward July 1." },
  { g: "Ship CardioMonitor 2.1", d: "Mayo beta sign-off is core to the June 30 target." },
];
const HIGHLIGHTS = [
  { d: "THU · MAY 7", t: "UCSF's adoption was more bottom-up than top-down." },
  { d: "WED · MAY 6", t: "First full block of deep work in two weeks." },
];

function Pri({ p }: { p: string }) {
  const isP1 = p === "P1";
  return (
    <span
      className="rounded px-1.5 py-0.5 font-mono text-[8px] tracking-wide"
      style={{ background: isP1 ? "var(--terracotta)" : "var(--moss)", color: "var(--paper)" }}
    >
      {p}
    </span>
  );
}

/* Small four-node loop ring with the current step filled. */
function MiniRing({ activeLabel }: { activeLabel: string }) {
  const pts = [
    { x: 50, y: 12 },
    { x: 88, y: 50 },
    { x: 50, y: 88 },
    { x: 12, y: 50 },
  ];
  return (
    <div className="relative mx-auto h-[92px] w-[92px]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
        <circle cx="50" cy="50" r="38" fill="none" stroke="var(--rule)" strokeWidth="1" strokeDasharray="2 4" />
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={i === 0 ? 6 : 4}
            fill={i === 0 ? "var(--terracotta)" : "var(--paper)"}
            stroke={i === 0 ? "var(--terracotta)" : "var(--rule)"}
            strokeWidth="1"
          />
        ))}
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <span className="font-display italic text-[8px] leading-tight text-[var(--ink-soft)]">
          NOW<br />
          <span className="text-[var(--ink)]">{activeLabel}</span>
        </span>
      </div>
    </div>
  );
}

/* ── A miniature of the live 3-column dashboard before planning ────── */
function DashboardMock() {
  return (
    <div
      className="relative w-full rounded-[20px] border overflow-hidden"
      style={{
        aspectRatio: "16 / 9",
        background: "var(--paper-2)",
        borderColor: "var(--hair)",
        boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 30px 70px rgba(26,24,21,0.10)",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-7 flex items-center px-3 gap-1.5 border-b z-10" style={{ borderColor: "var(--hair)", background: "var(--paper-2)" }}>
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
      </div>

      <div className="absolute inset-0 pt-7 grid grid-cols-[1fr_1.5fr_1.25fr]" style={{ background: "var(--paper)" }}>
        {/* LEFT — loop · upcoming · recent highlights */}
        <div className="border-r p-3 flex flex-col gap-3 overflow-hidden" style={{ borderColor: "var(--hair)" }}>
          <span className="font-mono text-[8px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">THE ALIGNMENT LOOP</span>
          <MiniRing activeLabel="The Daily Brief" />
          <span className="font-mono text-[7.5px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">UPCOMING</span>
          <span className="font-display italic text-[9px] text-[var(--ink-soft)]">Nothing scheduled.</span>
          <span className="font-mono text-[7.5px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">RECENT HIGHLIGHTS</span>
          {HIGHLIGHTS.map((h) => (
            <div key={h.d} className="flex flex-col">
              <span className="font-mono text-[7px] tracking-[0.12em] text-[var(--ink-soft)]">{h.d}</span>
              <span className="font-display italic text-[8.5px] leading-snug text-[var(--ink)]">{h.t}</span>
            </div>
          ))}
        </div>

        {/* CENTER — the daily brief */}
        <div className="p-3.5 flex flex-col gap-2 border-r overflow-hidden" style={{ borderColor: "var(--hair)" }}>
          <span className="font-mono text-[7.5px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">// JOURNAIL · THE DAILY BRIEF</span>
          <span className="font-display text-[13px] leading-snug text-[var(--ink)]" style={{ fontWeight: 360 }}>
            Good afternoon, Alex. <span className="italic text-[var(--terracotta)]">Let's begin.</span>
          </span>
          <span className="text-[9px] leading-snug text-[var(--ink-soft)]">Take 5 minutes to draft today together. First, think about:</span>
          <span className="flex gap-1.5"><span className="text-[var(--terracotta)] text-[9px]">→</span><span className="font-display italic text-[9.5px] leading-snug text-[var(--ink)]">What is the one thing that really has to move today?</span></span>
          <span className="flex gap-1.5"><span className="text-[var(--terracotta)] text-[9px]">→</span><span className="font-display italic text-[9.5px] leading-snug text-[var(--ink)]">What's quietly weighing on you that you'd like to clear?</span></span>
          <div className="mt-auto rounded-lg border px-2.5 py-1.5 flex items-center justify-between" style={{ borderColor: "var(--rule)", background: "var(--paper-2)" }}>
            <span className="font-display italic text-[8.5px] text-[var(--ink-soft)]/70">Ask the AI Assistant…</span>
            <span className="grid h-3.5 w-3.5 place-items-center rounded bg-[var(--terracotta)]/15 text-[var(--terracotta)] text-[8px]">→</span>
          </div>
        </div>

        {/* RIGHT — active tasks · goals */}
        <div className="p-3 flex flex-col gap-2 overflow-hidden">
          <span className="flex items-center justify-between">
            <span className="font-mono text-[8px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">ACTIVE TASKS</span>
            <span className="font-mono text-[7.5px] tracking-[0.12em] uppercase text-[var(--terracotta)]">12 OVERDUE</span>
          </span>
          {TASKS.slice(0, 4).map((tk) => (
            <div key={tk.t} className="flex items-start gap-1.5">
              <span className="mt-[3px] h-2 w-2 rounded-[3px] border shrink-0" style={{ borderColor: "var(--rule)" }} />
              <span className="min-w-0 flex flex-col">
                <span className="text-[9px] leading-snug text-[var(--ink)] truncate">{tk.t}</span>
                <span className="flex items-center gap-1"><Pri p={tk.p} /><span className="font-mono text-[7px] tracking-[0.1em] uppercase text-[var(--ink-soft)]">{tk.cat}</span></span>
              </span>
            </div>
          ))}
          <span className="mt-1 font-mono text-[8px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">GOALS</span>
          {GOALS.slice(0, 2).map((g) => (
            <span key={g} className="font-display italic text-[9px] leading-snug text-[var(--ink)] truncate">{g}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Act mock screens ─────────────────────────────────────────────── */
function ScreenFrame({ header, children }: { header: string; children: React.ReactNode }) {
  return (
    <div
      className="relative w-full max-w-[560px] mx-auto lg:mx-0 rounded-[18px] border overflow-hidden"
      style={{
        aspectRatio: "4 / 5",
        background: "var(--paper)",
        borderColor: "var(--hair)",
        boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-7 flex items-center px-3 gap-1.5 border-b z-10" style={{ borderColor: "var(--hair)", background: "var(--paper)" }}>
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="ml-2 font-mono text-[8px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">{header}</span>
      </div>
      <div className="absolute inset-0 pt-7 overflow-hidden">{children}</div>
    </div>
  );
}

function ReviewSelectMock() {
  return (
    <ScreenFrame header="Review & select">
      <div className="h-full px-4 py-3 flex flex-col gap-2.5 text-[var(--ink)]">
        <span className="font-display text-[14px]" style={{ fontWeight: 360 }}>Review &amp; suggest</span>
        <span className="font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">Goals with approaching deadlines</span>
        {GOAL_DEADLINES.map((g) => (
          <span key={g} className="flex gap-1.5"><span className="h-1 w-1 mt-1.5 rounded-full bg-[var(--terracotta)] shrink-0" /><span className="text-[9.5px] leading-snug text-[var(--ink-soft)]">{g}</span></span>
        ))}
        <span className="mt-1 font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--terracotta)]">Which tasks today?</span>
        {TASKS.slice(0, 4).map((tk, i) => (
          <span key={tk.t} className="flex items-center gap-2">
            <span className="grid h-3.5 w-3.5 place-items-center rounded-[4px] border shrink-0" style={{ borderColor: i < 3 ? "var(--terracotta)" : "var(--rule)", background: i < 3 ? "var(--terracotta)" : "transparent" }}>
              {i < 3 && <Check className="h-2.5 w-2.5 text-[var(--paper)]" strokeWidth={3} />}
            </span>
            <span className="text-[9.5px] leading-snug text-[var(--ink)] truncate flex-1">{tk.t}</span>
            <Pri p={tk.p} />
          </span>
        ))}
        <span className="mt-1 font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--terracotta)]">★ Suggestions from goals</span>
        {GOAL_SUGGESTIONS.map((s) => (
          <span key={s} className="flex items-center gap-2">
            <span className="h-3.5 w-3.5 rounded-[4px] border shrink-0" style={{ borderColor: "var(--rule)" }} />
            <span className="text-[9.5px] leading-snug text-[var(--ink-soft)] truncate">{s}</span>
          </span>
        ))}
        <span className="mt-auto flex items-center justify-between border-t pt-2" style={{ borderColor: "var(--hair)" }}>
          <span className="font-mono text-[8px] uppercase tracking-[0.12em] text-[var(--ink-soft)]">7 selected</span>
          <span className="rounded-md border px-2 py-1 font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--ink-soft)]" style={{ borderColor: "var(--rule)" }}>Rest → tomorrow</span>
        </span>
      </div>
    </ScreenFrame>
  );
}

function FocusMock() {
  const plan = TASKS;
  return (
    <ScreenFrame header="Today's focus">
      <div className="h-full px-4 py-3 flex flex-col gap-2 text-[var(--ink)]">
        <span className="flex items-center justify-between">
          <span className="font-display text-[14px]" style={{ fontWeight: 360 }}>Today's plan</span>
          <span className="font-mono text-[8px] tracking-[0.12em] uppercase text-[var(--ink-soft)]">2 / 7 · 29%</span>
        </span>
        {plan.map((tk, i) => {
          const done = i < 2;
          return (
            <span key={tk.t} className="flex items-center gap-2">
              <span className="grid h-3.5 w-3.5 place-items-center rounded-[4px] border shrink-0" style={{ borderColor: done ? "var(--moss)" : "var(--rule)", background: done ? "var(--moss)" : "transparent" }}>
                {done && <Check className="h-2.5 w-2.5 text-[var(--paper)]" strokeWidth={3} />}
              </span>
              <span className="text-[9.5px] leading-snug truncate flex-1" style={{ color: "var(--ink)", textDecoration: done ? "line-through" : "none", opacity: done ? 0.5 : 1 }}>{tk.t}</span>
              {!done && <Pri p={tk.p} />}
            </span>
          );
        })}
        <div className="mt-1 rounded-lg border px-2.5 py-1.5" style={{ borderColor: "var(--hair)", background: "var(--paper-2)" }}>
          <span className="font-mono text-[7px] tracking-[0.14em] uppercase text-[var(--moss)]">◆ FOCUS · Completed</span>
          <span className="block text-[9px] text-[var(--ink)]">Review FDA Q-Sub response draft</span>
        </div>
        <div className="rounded-lg border px-2.5 py-1.5" style={{ borderColor: "var(--hair)" }}>
          <span className="font-mono text-[7px] tracking-[0.14em] uppercase text-[var(--terracotta)]">✎ NOTE</span>
          <span className="block text-[9px] text-[var(--ink)]">I need to refine the draft to see if this will work.</span>
        </div>
        <div className="mt-auto grid place-items-center pt-1">
          <span className="rounded-full bg-[var(--terracotta)] px-4 py-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-[var(--terracotta-soft)]">Close the day →</span>
        </div>
      </div>
    </ScreenFrame>
  );
}

function JournalMock() {
  return (
    <ScreenFrame header="Journal · written">
      <div className="h-full px-4 py-3 flex flex-col gap-2 text-[var(--ink)] overflow-hidden">
        <span className="font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">// CLOSED</span>
        <span className="font-display text-[15px] leading-snug" style={{ fontWeight: 360 }}>
          Today's story is <span className="italic text-[var(--terracotta)]">written.</span>
        </span>
        <div className="rounded-lg border-l-2 px-2.5 py-1.5" style={{ borderColor: "var(--terracotta)", background: "color-mix(in srgb, var(--terracotta) 8%, transparent)" }}>
          <span className="font-mono text-[7px] tracking-[0.14em] uppercase text-[var(--terracotta)]">★ PROUD OF</span>
          <span className="block font-display italic text-[10px] leading-snug text-[var(--ink)]">Today was the best day for regulatory issues!</span>
        </div>
        <span className="font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">Completed · 2</span>
        <span className="text-[9px] leading-snug text-[var(--ink-soft)]">Review FDA Q-Sub draft · Send VP Engineering offer to Maya</span>
        <span className="font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">Goal connections</span>
        {GOAL_CONN.map((c) => (
          <span key={c.g} className="text-[9px] leading-snug text-[var(--ink-soft)]">
            <span className="font-medium text-[var(--ink)]">{c.g}:</span> {c.d}
          </span>
        ))}
        <span className="mt-auto font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--ink-soft)]">Summary</span>
        <span className="text-[9px] leading-snug text-[var(--ink-soft)]">Focused on the biggest bottlenecks and made real progress on regulatory and hiring.</span>
      </div>
    </ScreenFrame>
  );
}

function ActMock({ index }: { index: number }) {
  if (index === 0) return <ReviewSelectMock />;
  if (index === 1) return <FocusMock />;
  return <JournalMock />;
}

export function DailyRhythmDeepDive() {
  const { t, lang } = useTranslation();
  const en = lang === "en";
  const d = t.landing.cover.dailyRhythmPage;

  return (
    <>
      {/* ── Dashboard anatomy ───────────────────────────────────────── */}
      <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-[100px] z-[2]">
        <div className="max-w-[760px]">
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {d.anatomy.kicker}
          </div>
          <h2 className="font-display text-[clamp(30px,3.6vw,48px)] leading-[1.06] tracking-[-0.02em] text-balance" style={{ fontWeight: 380 }}>
            {d.anatomy.h2a} <em className="text-[var(--terracotta)] italic">{d.anatomy.h2em}</em>
          </h2>
          <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] text-pretty">{d.anatomy.description}</p>
          <p className="mt-4 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] text-pretty">
            {en
              ? "At its core is structured daily reflection that turns scattered notes into one clear thread you can act on."
              : "V jedru je strukturirana dnevna refleksija, ki razpršene zapiske spremeni v eno jasno nit, na kateri lahko gradiš."}
          </p>
        </div>

        <div className="mt-10">
          <DashboardMock />
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {d.anatomy.columns.map((col) => (
            <div key={col.tag} className="border-t border-[var(--rule)] pt-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--terracotta)]">{col.tag}</span>
              <h3 className="mt-2 font-display text-[19px] leading-snug text-[var(--ink)]" style={{ fontWeight: 360 }}>{col.title}</h3>
              <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--ink-soft)]">{col.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── The full-day process ────────────────────────────────────── */}
      <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-[100px] z-[2]">
        <div className="max-w-[760px] mb-14">
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {d.process.kicker}
          </div>
          <h2 className="font-display text-[clamp(30px,3.6vw,48px)] leading-[1.06] tracking-[-0.02em] text-balance" style={{ fontWeight: 380 }}>
            {d.process.h2a} <em className="text-[var(--terracotta)] italic">{d.process.h2em}</em>
          </h2>
          <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] text-pretty">{d.process.description}</p>
        </div>

        <div className="flex flex-col gap-20 md:gap-28">
          {d.process.acts.map((act, i) => (
            <div key={act.no} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              {/* Copy + steps */}
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-[var(--terracotta)] text-[28px] leading-none" style={{ fontWeight: 360 }}>{act.no}</span>
                  <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--ink-soft)]">{act.phase}</span>
                </div>
                <h3 className="mt-3 font-display text-[clamp(23px,2.5vw,32px)] leading-[1.12] tracking-[-0.02em] text-[var(--ink)]" style={{ fontWeight: 350 }}>
                  {act.title}
                </h3>
                <p className="mt-3 text-[15.5px] leading-[1.65] text-[var(--ink-soft)] max-w-[480px] text-pretty">{act.body}</p>
                <ol className="mt-6 flex flex-col gap-3.5">
                  {act.steps.map((step, si) => (
                    <li key={si} className="flex gap-3">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-[var(--terracotta)]/40 bg-[var(--terracotta)]/[0.06] font-mono text-[9px] text-[var(--terracotta)]">
                        {si + 1}
                      </span>
                      <span className="text-[14.5px] leading-[1.55] text-[var(--ink)] text-pretty">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Mock screen */}
              <div>
                <ActMock index={i} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
